import {
    Box,
    FormHelperText,
    SxProps,
    Theme,
    styled,
    IconButton,
} from '@mui/material';
import { colors } from 'common/constants';
import React from 'react';
import { Clear as ClearIcon } from '@mui/icons-material';
import ImageIcon from '@mui/icons-material/Image';
import { DropzoneRootProps, useDropzone } from 'react-dropzone';
import {
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
    UseFormStateReturn,
    useFormContext,
} from 'react-hook-form';
interface IProps {
    name: string;
    sx?: SxProps;
    multiple?: boolean;
    maxFileSize?: number;
    isDelete?: boolean;
}
const getDropzoneColor = (props: DropzoneRootProps, theme: Theme) => {
    // console.log(props.isDragAccept);
    if (props.isDragAccept) {
        return theme.palette.success.main;
    }
    if (props.isDragReject) {
        return theme.palette.error.main;
    }
    if (props.isFocused) {
        return theme.palette.primary.main;
    }
    return colors.main;
};
const Dropzone = styled('div')(({ theme, ...props }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '15rem',
    height: '15rem',
    borderColor: getDropzoneColor(props, theme),
    borderStyle: 'dashed',
    borderWidth: '2px',
    backgroundColor: '#F4EFFF',
    outline: 'none',
    transition: 'border .24s ease-in-out',
    borderRadius: '17px',
    [theme.breakpoints.up('xl')]: {
        width: '322px',
        height: '322px',
    },
}));
const UploadImage: React.FC<IProps> = ({
    name,
    multiple,
    sx,
    maxFileSize,
    isDelete,
}) => {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState, formState }) => (
                <ImageUploadRender
                    field={field}
                    fieldState={fieldState}
                    formState={formState}
                    sx={sx}
                    multiple={multiple}
                    maxFileSize={maxFileSize}
                    isDelete={isDelete}
                />
            )}
        />
    );
};

const ImageUploadRender = ({
    field,
    multiple,
    sx,
    fieldState,
    maxFileSize,
    isDelete,
}: {
    field: ControllerRenderProps<FieldValues, string>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FieldValues>;
    sx?: SxProps;
    multiple?: boolean;
    maxFileSize?: number;
    isDelete?: boolean;
}) => {
    const onDrop = (acceptedFiles: File[]) => {
        if (multiple) {
            field.onChange([...(field.value || []), ...acceptedFiles]);
        } else {
            field.onChange(acceptedFiles[0]);
        }
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        onDrop,
        multiple: multiple,
    });

    const renderImage = (file: File | string) => {
        if (typeof file === 'string') {
            return (
                <img
                    key={file}
                    src={file}
                    alt={file}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '17px',
                    }}
                />
            );
        } else {
            return (
                <Box
                    sx={{ position: 'relative', width: '100%', height: '100%' }}
                >
                    <img
                        key={file?.name}
                        src={file ? URL.createObjectURL(file) : ''}
                        alt={file?.name}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '17px',
                        }}
                    />
                    {isDelete && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                field.onChange(null);
                            }}
                            sx={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '50%',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                },
                            }}
                        >
                            <ClearIcon />
                        </IconButton>
                    )}
                </Box>
            );
        }
    };

    return (
        <>
            <Dropzone
                {...getRootProps({
                    isFocused,
                    isDragAccept,
                    isDragReject,
                    sx: {
                        ...sx,
                    },
                })}
            >
                <input {...getInputProps()} />
                {multiple
                    ? field?.value?.map((file: File) => renderImage(file))
                    : field?.value && renderImage(field?.value)}
                {!field?.value && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: 2,
                            textAlign: 'center',
                        }}
                    >
                        <Box>
                            <ImageIcon
                                sx={{
                                    color: 'var(--color-primary)',
                                    fontSize: ['5rem', '5rem', '6rem', '8rem'],
                                    mt: '2rem',
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                color: colors.main,
                                fontWeight: 700,
                                fontSize: '18px',
                            }}
                        >
                            Chọn ảnh
                        </Box>
                        <Box
                            sx={{
                                color: '#737373',
                                fontStyle: 'italic',
                                fontWeight: 300,
                                fontSize: [
                                    '0.7rem',
                                    '0.7rem',
                                    '0.7rem',
                                    '1rem',
                                    '1rem',
                                ],
                                padding: '0 57px',
                            }}
                        >
                            Định dạng file .png, .jpg, .jpeg và không quá{' '}
                            {maxFileSize}MB
                        </Box>
                    </Box>
                )}
            </Dropzone>
            {!!fieldState.error && (
                <FormHelperText
                    error={!!fieldState.error}
                    sx={{
                        fontSize: '14px',
                        marginLeft: 0,
                        color: `${colors.textError} !important`,
                        fontWeight: '400',
                    }}
                >
                    {fieldState.error && fieldState.error?.message}
                </FormHelperText>
            )}
        </>
    );
};

export default UploadImage;
